import L from 'leaflet';
import {
  Battery,
  ChevronRight,
  Clock,
  MapPinned,
  Navigation,
  Route as RouteIcon,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { type JourneyDto, useJourneyPoints, useJourneys } from '../api/useJourneys.js';
import { useRoster } from '../api/useRoster.js';
import { EmptyState } from '../components/EmptyState.js';
import { cn } from '../lib/cn.js';
import { formatDateTime } from '../lib/format.js';

// Journey monitoring page.
//
// Left pane: worker + date picker, scrollable list of journeys for that window.
// Right pane: Leaflet map. Picking a journey draws the polyline from /journeys/points
// bounded to that journey's [start_at, end_at] window.

const FALLBACK_CENTER: [number, number] = [-33.8688, 151.2093]; // Sydney CBD

export default function Journeys() {
  const roster = useRoster();
  const workers = useMemo(
    () => (roster.data?.members ?? []).filter((m) => m.app_role === 'worker'),
    [roster.data],
  );

  const [userId, setUserId] = useState<string | undefined>(undefined);
  const [date, setDate] = useState<string>(() => toDateInputValue(new Date()));
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Auto-select the first worker if the user hasn't picked one yet.
  useEffect(() => {
    if (!userId && workers.length > 0) setUserId(workers[0]?.user_id);
  }, [workers, userId]);

  // [from, to] in UTC derived from the picked local date. Covers a 26-hour span to be
  // safe across DST transitions in AEDT/AEST.
  const window = useMemo(() => {
    const start = new Date(`${date}T00:00:00`);
    const end = new Date(start.getTime() + 24 * 60 * 60_000);
    return { from: start.toISOString(), to: end.toISOString() };
  }, [date]);

  const journeys = useJourneys({ userId, from: window.from, to: window.to });

  const selected = (journeys.data?.journeys ?? []).find((j) => j.id === selectedId) ?? null;
  const points = useJourneyPoints({
    userId: selected?.user_id,
    from: selected?.start_at,
    to: selected?.end_at,
  });

  // Auto-select the first journey once a new result arrives.
  useEffect(() => {
    const first = journeys.data?.journeys[0];
    if (first && !journeys.data?.journeys.some((j) => j.id === selectedId)) {
      setSelectedId(first.id);
    }
    if ((journeys.data?.journeys.length ?? 0) === 0) setSelectedId(null);
  }, [journeys.data, selectedId]);

  return (
    <div className="mx-auto flex h-[calc(100vh-7rem)] max-w-7xl flex-col gap-4">
      <div className="flex items-end justify-between">
        <div>
          <span className="eyebrow">Operations</span>
          <h1 className="mt-1 font-heading text-[22px] font-bold tracking-tightest text-ink">
            Journey monitoring
          </h1>
          <p className="text-sm text-ink-faint">
            Per-worker daily driving journeys — sessionized from location history. Idle gaps ≥ 20
            min start a new journey, and only sessions averaging ≥ 25 km/h are shown (walking around
            a single client's home is filtered out).
          </p>
        </div>
        <div className="flex items-end gap-3">
          <div>
            <label htmlFor="jworker" className="mb-1 block text-[11px] font-medium text-ink-faint">
              Worker
            </label>
            <select
              id="jworker"
              value={userId ?? ''}
              onChange={(e) => setUserId(e.target.value)}
              className="min-w-[12rem] rounded-md border border-line-strong bg-white px-3 py-1.5 text-sm focus:border-brand-purple focus:outline-none"
            >
              <option value="" disabled>
                Select a worker
              </option>
              {workers.map((w) => (
                <option key={w.user_id} value={w.user_id}>
                  {w.display_name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="jdate" className="mb-1 block text-[11px] font-medium text-ink-faint">
              Date
            </label>
            <input
              id="jdate"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="rounded-md border border-line-strong bg-white px-3 py-1.5 text-sm focus:border-brand-purple focus:outline-none"
            />
          </div>
        </div>
      </div>

      <div className="grid flex-1 min-h-0 grid-cols-1 gap-4 lg:grid-cols-[22rem_1fr]">
        <div className="flex min-h-0 flex-col panel">
          <div className="border-b border-line px-4 py-3 text-sm font-semibold text-ink">
            {journeys.data?.journeys.length ?? 0} journeys
          </div>
          <div className="flex-1 overflow-y-auto">
            {!userId && (
              <EmptyState
                icon={RouteIcon}
                title="Pick a worker"
                description="Choose a worker from the dropdown above to see their journeys."
              />
            )}
            {userId && journeys.isLoading && (
              <div className="p-4 text-sm text-ink-faint">Loading journeys…</div>
            )}
            {userId && journeys.isError && (
              <div className="m-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
                {(journeys.error as Error).message}
              </div>
            )}
            {userId && !journeys.isLoading && (journeys.data?.journeys.length ?? 0) === 0 && (
              <EmptyState
                icon={MapPinned}
                title="No movement this day"
                description="This worker has no location points that resolve into a journey for the selected date. Try another date or check that location sharing is enabled."
              />
            )}
            <ul className="divide-y divide-line">
              {(journeys.data?.journeys ?? []).map((j) => (
                <li key={j.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedId(j.id)}
                    className={cn(
                      'flex w-full items-center gap-3 px-4 py-3 text-left transition hover:bg-surface-3',
                      selectedId === j.id && 'bg-brand-purple/5',
                    )}
                  >
                    <JourneyIcon active={selectedId === j.id} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-ink">
                        {formatTime(j.start_at)} → {formatTime(j.end_at)}
                      </div>
                      <div className="mt-0.5 flex items-center gap-3 text-[11px] text-ink-faint">
                        <span
                          className="inline-flex items-center gap-1"
                          title={
                            j.distance_source === 'matched'
                              ? 'Snapped to roads via OSRM'
                              : 'Approximate — straight lines between GPS samples'
                          }
                        >
                          <Navigation className="h-3 w-3" />
                          {j.distance_source === 'polyline' ? '≈ ' : ''}
                          {formatDistance(j.distance_m)}
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {formatDuration(j.duration_seconds)}
                        </span>
                        <span>{j.point_count} pts</span>
                      </div>
                      {(j.battery_min !== null || j.battery_max !== null) && (
                        <div className="mt-1 inline-flex items-center gap-1 text-[10px] text-ink-faint">
                          <Battery className="h-3 w-3" />
                          {j.battery_max ?? '—'}% → {j.battery_min ?? '—'}%
                        </div>
                      )}
                    </div>
                    <ChevronRight className="h-4 w-4 text-ink-mute" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="relative min-h-0 overflow-hidden rounded-lg bg-surface-3 shadow-panel">
          <JourneyMap journey={selected} points={points.data?.points ?? []} />
          {selected && (
            <div className="pointer-events-none absolute left-4 top-4 rounded-md bg-white/95 px-3 py-2 text-xs shadow ring-1 ring-gray-200">
              <div className="font-semibold text-ink">
                {formatDateTime(selected.start_at)} → {formatDateTime(selected.end_at)}
              </div>
              <div
                className="mt-0.5 text-ink-faint"
                title={
                  selected.distance_source === 'matched'
                    ? 'Snapped to roads via OSRM'
                    : 'Approximate — straight lines between GPS samples'
                }
              >
                {selected.distance_source === 'polyline' ? '≈ ' : ''}
                {formatDistance(selected.distance_m)} · {formatDuration(selected.duration_seconds)}{' '}
                · {selected.point_count} fixes
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function JourneyMap({
  journey,
  points,
}: {
  journey: JourneyDto | null;
  points: { lat: number; lng: number; recorded_at: string }[];
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const polylineRef = useRef<L.Polyline | null>(null);
  const startRef = useRef<L.CircleMarker | null>(null);
  const endRef = useRef<L.CircleMarker | null>(null);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = L.map(containerRef.current, {
      center: FALLBACK_CENTER,
      zoom: 11,
      zoomControl: true,
    });
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(map);
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    // Clear any previous layers before drawing the new journey.
    if (polylineRef.current) polylineRef.current.remove();
    if (startRef.current) startRef.current.remove();
    if (endRef.current) endRef.current.remove();

    if (!journey || points.length === 0) return;

    const coords: [number, number][] = points.map((p) => [p.lat, p.lng]);
    const line = L.polyline(coords, { color: '#4C12A1', weight: 4, opacity: 0.85 }).addTo(map);
    polylineRef.current = line;

    // Start marker (green) + end marker (purple) framing the polyline.
    const first = coords[0];
    const last = coords[coords.length - 1];
    if (first) {
      startRef.current = L.circleMarker(first, {
        radius: 7,
        color: '#10b981',
        fillColor: '#10b981',
        fillOpacity: 0.9,
        weight: 3,
      })
        .bindPopup('Start')
        .addTo(map);
    }
    if (last) {
      endRef.current = L.circleMarker(last, {
        radius: 7,
        color: '#4C12A1',
        fillColor: '#4C12A1',
        fillOpacity: 0.9,
        weight: 3,
      })
        .bindPopup('End')
        .addTo(map);
    }

    map.fitBounds(line.getBounds(), { padding: [40, 40] });
  }, [journey, points]);

  return <div ref={containerRef} className="h-full w-full" />;
}

function JourneyIcon({ active }: { active: boolean }) {
  return (
    <div
      className={cn(
        'flex h-9 w-9 items-center justify-center rounded-md',
        active ? 'bg-brand-purple text-white' : 'bg-surface-3 text-ink-faint',
      )}
    >
      <RouteIcon className="h-4 w-4" />
    </div>
  );
}

function formatDistance(m: number): string {
  if (m < 1000) return `${Math.round(m)} m`;
  return `${(m / 1000).toFixed(m < 10_000 ? 2 : 1)} km`;
}

function formatDuration(secs: number): string {
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins} min`;
  const hrs = Math.floor(mins / 60);
  const rem = mins % 60;
  return rem > 0 ? `${hrs}h ${rem}m` : `${hrs}h`;
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
}

function toDateInputValue(d: Date): string {
  // YYYY-MM-DD in the user's local time (what <input type="date"> expects).
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}
